import { motion } from "framer-motion";
import { ArrowRight, CalendarRange, Clock, MapPin, Plane, Route } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

const SERVICES = [
  {
    num: "01",
    icon: Clock,
    title: "Hourly Rentals",
    copy: "Weddings, meetings, errands, full-day city work — the cab waits so you don't have to. Sedan ₹30/km (₹300/hr), Ertiga ₹45/km (₹450/hr), Innova ₹55/km (₹550/hr) — kilometres and hours both counted: whichever is higher is what you pay.",
    journey: "Hourly Rental",
    accent: true,
    span: "md:col-span-7",
  },
  {
    num: "02",
    icon: Plane,
    title: "Airport to Airport Transfers",
    copy: "Terminal to terminal, takeoff to touchdown. Early flights and late landings — the pickup adjusts to the schedule, never the other way round.",
    journey: "Airport Transfer",
    span: "md:col-span-5",
  },
  {
    num: "03",
    icon: Route,
    title: "Long Distance Journeys",
    copy: "Outstation runs and interstate highways. One way or round trip — your driver and your cab stay yours from the first kilometre to the last. Sedan ₹14/km · Ertiga ₹15/km · Innova ₹18/km · Crysta ₹22/km.",
    journey: "Long Distance Journey",
    span: "md:col-span-5",
  },
  {
    num: "04",
    icon: MapPin,
    title: "Local Intercity",
    copy: "Point-to-point city runs under 300 km. Fixed day packages — sedan ₹1,000/day, Ertiga ₹1,500/day, Innova ₹1,800/day, Crysta ₹2,200/day — kilometre billing from ₹12/km.",
    journey: "Local Intercity",
    span: "md:col-span-7",
  },
  {
    num: "05",
    icon: CalendarRange,
    title: "Multi-Day Tour Packages",
    copy: "Curated multi-day circuits across Odisha, Chhattisgarh, Jharkhand and West Bengal — every destination listed with the days it takes and the full price, written up front. The package list is being compiled; ask on WhatsApp for early details.",
    journey: "Tour Package",
    span: "md:col-span-12",
  },
];

function Card({ s, i, onBook }) {
  const Icon = s.icon;
  return (
    <motion.article
      initial={{ opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.65, ease: EASE, delay: (i % 2) * 0.1 }}
      data-testid={`service-card-${i + 1}`}
      className={`relative p-8 md:p-10 border flex flex-col min-h-[280px] ${s.span} ${
        s.accent
          ? "bg-signal text-ink border-signal"
          : "bg-surface text-bone border-white/10 hover:border-signal/50 transition-colors duration-300"
      }`}
    >
      <span className={`absolute top-8 right-8 font-mono text-xs tracking-[0.2em] ${s.accent ? "text-ink/60" : "text-signal"}`}>
        ({s.num})
      </span>
      <span className={`inline-flex items-center justify-center w-12 h-12 mb-6 ${s.accent ? "bg-ink text-signal" : "bg-signal/10 text-signal border border-signal/40"}`}>
        <Icon size={22} strokeWidth={1.8} />
      </span>
      <h3 className="font-display uppercase text-2xl md:text-3xl leading-tight mb-4">{s.title}</h3>
      <p className={`text-sm md:text-base leading-relaxed ${s.accent ? "text-ink/75" : "text-zinc-400"}`}>
        {s.copy}
      </p>
      <button
        data-testid={`service-book-${i + 1}`}
        onClick={() => onBook(s.journey)}
        className={`mt-auto pt-6 self-start inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-200 ${
          s.accent ? "text-ink hover:text-paper" : "text-bone hover:text-signal"
        }`}
      >
        Book this ride
        <ArrowRight size={14} />
      </button>
    </motion.article>
  );
}

export default function Services({ onBook }) {
  return (
    <section id="services" className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
      <div className="mb-14 md:mb-20">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-signal mb-4">(01) — What we drive for you</p>
        <h2 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
          Five ways
          <br />
          to move.
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        {SERVICES.map((s, i) => (
          <Card key={s.num} s={s} i={i} onBook={onBook} />
        ))}
      </div>
    </section>
  );
}
